import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react"
import Skeleton from "react-loading-skeleton";
import { AnyPeriod, useIsMounted } from "@ct-react/core";
import { LocaleLink, useLocaleNavigate } from "@ct-react/locale";
import { ArticleType, ArticleUrlData, buildArticleUrl, buildSearchUrl } from "@shared/url";
import { isRentalArticle, isSaleArticle } from "@shared/graphql";
import { actions, article } from "../i18n/sharable-defs";
import { parse, rebuild } from "../tools/search-params/open-article";
import NotFoundPage from "./not-found";
import { Wrapper } from "../components/layout/wrapper";
import SeoMetas from "../components/layout/seo-metas";
import Button from "../components/common/button";
import TitlePart from "../components/article/title";
import ImagesPart from "../components/article/images";
import LocationPart from "../components/article/location";
import DescriptionPart from "../components/article/description";
import RentalBookingBox from "../components/article/on-interest/rental-booking";
import AnyContactingBox from "../components/article/on-interest/any-contacting";
import EquipmentPart from "../components/article/equipment";
import SpriteSvg from "../components/common/svg";
import ArticleCarousel from "../components/carousel/article-carousel";
import Reviews from "../components/article/reviews";

import "./open-article.scss";

const transDefs = defineMessages({
  description: { id: "article.rubrics.description", defaultMessage: "Description" },
  location: { id: "article.rubrics.location", defaultMessage: "Emplacement" },
  sale_carousel_title: { id: "article.section.sale-carousel-title", defaultMessage: "Nos autres biens à vendre" },
  rental_carousel_title: { id: "article.section.rental-carousel-title", defaultMessage: "Nos autres biens à louer" },
  carousel_button: { id: "article.section.carousel-button", defaultMessage: "Découvrez nos autres propriétés" },
});

const SEO_ARTICLE_GQL_DATA = gql`
  query ArticleSEO($articleId: ID!) {
    forSeo: articleDetail(articleId: $articleId) {
      ... on RentalAccommodationArticle {
        id slug
        seo { title { value } description { value } image keywords }
      }
      ... on SeasonalAccommodationArticle {
        id slug
        seo { title { value } description { value } image keywords }
      }
      ... on AnnualAccommodationArticle {
        id slug
        seo { title { value } description { value } image keywords }
      }
      ... on SaleAccommodationArticle {
        id slug
        seo { title { value } description { value } image keywords }
      }
    }
  }
`;

const FULL_ARTICLE_GQL_DATA = gql`
  query GetArticle($articleId: ID!) {
    detail: articleDetail(articleId: $articleId) {
      ... on RentalAccommodationArticle {
        id
        title { value }
        technicalName
        referenceId
        description { value matchLang }
        classification
        images { aspectRatio assets { url breakpoint } }
        address { resume }
        coordinates
        lowestPrice { type onDemand value }
        virtualVisits {
            url
            alternativeText {
              matchLang
              value
            }
            title {
              matchLang
              value
            }
        }
        features {
          bedrooms
          bathrooms
          maxGuests
          isSmokerCompliant
          isPetCompliant
          withBalcony
          withFondueSet
          withHairDryer
          withLift
          withMicrowave
          withParking
          withRacletteSet
          withSauna
          withTV
          withWashingMachine
          withWIFI
          nearSkiArea
          onShoppingArea
          nearBusStop
        }
        touristTax {
          optionId
          grid { ageCategory ageRange price }
        }
      }
      ... on SeasonalAccommodationArticle {
        id
        title { value }
        referenceId
        description { value matchLang }
        images { aspectRatio assets { url breakpoint } }
        address { resume }
        coordinates
        seasonalPrice: price { isMonthly onDemand value }
        features {
          bedrooms
          bathrooms
          maxGuests
          isPetCompliant
          withBalcony
          withLift
          withParking
          withSauna
          withTV
          withWashingMachine
          withWIFI
          nearSkiArea
          onShoppingArea
        }
      }
      ... on AnnualAccommodationArticle {
        id
        title { value }
        referenceId
        description { value matchLang }
        images { aspectRatio assets { url breakpoint } }
        address { resume }
        coordinates
        annualPrice: price { onDemand value }
        features {
          bedrooms
          bathrooms
          withBalcony
          withLift
          withParking
          withSauna
          withTV
          withWashingMachine
          withWIFI
        }
      }
      ... on SaleAccommodationArticle {
        id
        title { value }
        referenceId
        description { value matchLang }
        images { aspectRatio assets { url breakpoint } }
        address { resume }
        coordinates
        sold
        salePrice: price { onDemand value }
        features {
          constructionYear
          livingSpace
          orientation
          bathrooms
          bedrooms
          floor {
            matchLang
            value
          }
          heatings
          withParking
          isSellableToForeigners
          renovationFund
          isFurnished
          charges
        }
      }
    }
  }
`;

const normalizeArticlePrice = (article: any) => {
  if (!article) return article;
  const { seasonalPrice, annualPrice, salePrice, ...detail } = article;
  return {
    ...detail,
    price: seasonalPrice ?? annualPrice ?? salePrice
  };
};

const REVIEW_ARTICLE_GQL_DATA = gql`
  query ReviewArticle($articleId: ID!) {
    bookingAccommodationReviews(articleId: $articleId) {
      averageRate
      commentsCount
      ratesCount
      comments {
        averageRate
        comment
        customerName
        reviewedAt
        withAnswer
        answer {
          answeredAt
          comment
        }
      }
      rates {
        accuracy
        bedsComfort
        cleanliness
        location
        overallComfort
        reservation
        satisfaction
        value
        welcome
      }
    }
  }
`;

const OpenArticlePage = () => {

  const intl = useIntl();
  const isMounted = useIsMounted();
  const navigate = useLocaleNavigate();

  const [ searchParams, setSearchParams ] = useSearchParams();

  const [ failure, setFailure ] = useState<boolean>(!parse(searchParams).id);
  const [ articleId, setArticleId ] = useState<string | undefined>(parse(searchParams).id);
  const [ periodPreset, setPeriodPreset ] = useState<AnyPeriod | undefined>(parse(searchParams).bookingPeriod);

  const { loading: seoLoading, data: seoData } = useQuery<any>(SEO_ARTICLE_GQL_DATA, {
    ssr: true,
    variables: { articleId }
  });

  const { loading, data: queriedData, error } = useQuery<any>(FULL_ARTICLE_GQL_DATA, {
    skip: !articleId,
    ssr: false,
    variables: { articleId },
  });
  const data = queriedData && {
    ...queriedData,
    detail: normalizeArticlePrice(queriedData.detail)
  };

  const { loading: reviewsLoading, data: reviews, error: reviewsError } = useQuery<any>(REVIEW_ARTICLE_GQL_DATA, {
    skip: !articleId,
    variables: { articleId },
    ssr: false,
  });

  const rebuildUrl = useCallback((article: ArticleUrlData) => buildArticleUrl(article), []);

  useEffect(() => {
    !!error && setFailure(true);
  }, [ error ]);

  useEffect(() => {
    if (!isMounted) return;
    const id = parse(searchParams).id;
    if (articleId === id) return;
    !!id ? setArticleId(id) : setFailure(true);
  }, [ searchParams ]);

  useEffect(() => {
    rebuild(periodPreset, searchParams);
    setSearchParams(searchParams, { replace: true });
  }, [ periodPreset ]);

  if (failure)
    return (<NotFoundPage />);

  return (<>
      <article className="open-article">

        {(!seoLoading && !!seoData?.forSeo) &&
          <SeoMetas {...seoData.forSeo.seo}
                    canonical={buildArticleUrl(seoData.forSeo)} />
        }

        <Wrapper.Centered className="article-head">
          {(loading)
            ? <Skeleton width={100} className="btn" />
            : <Button className="back with-icon link" onClick={() => navigate(-1)}>
                <SpriteSvg id="chevron-left" className="chevron"/>
                {intl.formatMessage(actions.back)}
              </Button>
          }
          <TitlePart loading={loading}
                    {...(!!data?.detail) && {
                      data: {
                        title: data.detail.title,
                        subTitle: <></>,
                        url: rebuildUrl(data.detail),
                        details: data.detail
                      }
                    }}/>
          <ImagesPart key={articleId}
                      loading={loading}
                      {...(!!data?.detail && {
                        images: data.detail.images,
                        data: data.detail
                      })} />
        </Wrapper.Centered>

        <Wrapper.Centered className="two-col">

          <div className="left">
            <DescriptionPart loading={loading}
                            title={intl.formatMessage(transDefs.description)}
                            {...(!!data?.detail && {
                              data: {
                                referenceId: data.detail.referenceId,
                                text: data.detail.description,
                                classification: data.detail.classification
                              }
                            })}/>
            <EquipmentPart loading={loading}
                            title={intl.formatMessage(article.equipment)}
                            {...(!!data?.detail && {
                              data: {
                                detail: data.detail
                              }
                            })}/>
          </div>

          <aside className="right">
            {(!loading && !!articleId && !!data?.detail) &&
              <>
                {(() => {
                  if (isRentalArticle(data.detail))
                    return (<RentalBookingBox config={{
                                                articleId,
                                                articleData: { technicalName: data.detail.technicalName },
                                                presetPeriod: periodPreset,
                                                maxGuests: data.detail.features.maxGuests,
                                                touristTaxConfig: data.detail.touristTax
                                              }}
                                              onChoice={setPeriodPreset}
                                              image={data.detail.images[6] || null}/>);
                  return (<AnyContactingBox articleId={articleId}
                                            type={data.detail.__typename}
                                            price={data.detail.price} />);
                })()}
              </>
            }
          </aside>

        </Wrapper.Centered>

        <Wrapper.Centered className="article-foot">
          <LocationPart loading={loading}
                        title={intl.formatMessage(transDefs.location)}
                        {...(!!data?.detail && {
                          data: {
                            address: data.detail.address,
                            coordinates: data.detail.coordinates,
                            features: data.detail.features
                          }
                        })} />
          {(!loading && !!articleId && !!data?.detail) &&
              <>
                {(() => {
                  if (isRentalArticle(data.detail) && (reviewsLoading || !!reviews?.bookingAccommodationReviews))
                    return <Reviews loading={reviewsLoading} data={reviews} />
                })()}
              </>
            }

        </Wrapper.Centered>

      </article>

      {(!loading && !!data?.detail) &&
        <Wrapper.Centered className="suggestion-carousel">
          <h2>
            {isRentalArticle(data.detail) && intl.formatMessage(transDefs.rental_carousel_title)}
            {isSaleArticle(data.detail) && intl.formatMessage(transDefs.sale_carousel_title)}
          </h2>
          <ArticleCarousel type={data.detail.__typename}/>
          <LocaleLink to={buildSearchUrl(data.detail.__typename)} className="btn primary with-icon">
            {intl.formatMessage(transDefs.carousel_button)}
            <SpriteSvg id="chevron-right" className="icon"/>
          </LocaleLink>
        </Wrapper.Centered>
      }

  </>);

};

export default OpenArticlePage;
